import { useState, useEffect, useMemo } from 'react'
import _ from 'lodash'
import useRatingCriteria from './useRatingCriteria'

const generateRatesData = (ratingFields, usersHaveRate, proposalId) => {
  return ratingFields.map(item => {
    const outputObject = {}
    usersHaveRate.forEach(user => {
      const rateObj = user.proposals[proposalId].rate
      const rate = rateObj[item.name]
      outputObject.name = item.label
      outputObject[user.firstName] = rate || null
    })
    return outputObject
  })
}

const generateCalcData = ratingFields => {
  const calcData = {}
  ratingFields.forEach(item => {
    calcData[item.name] = {
      count: 0,
      total: 0
    }
  })
  return calcData
}

export const getProposalAllRate = (users, proposalId, ratingCriteria) => {
  const judges = users.filter(value => value.role === 'judge')
  const usersHaveRate = judges.filter(value =>
    _.get(value, `proposals.${proposalId}.rate`, false)
  )

  if (usersHaveRate.length === 0) {
    return { rates: [], rate: {}, userVoted: 0, users: judges.length }
  }

  const rates = generateRatesData(ratingCriteria, usersHaveRate, proposalId)
  const userVoted = usersHaveRate.length
  const calcData = generateCalcData(ratingCriteria)
  const rate = {}

  // Count times a criterion is rated and its total value
  usersHaveRate.forEach(user => {
    const userRate = user.proposals[proposalId].rate
    Object.entries(userRate).forEach(([key, value]) => {
      if (value) {
        calcData[key].count++
        calcData[key].total += value
      }
    })
  })

  // Calculate average ratings
  Object.entries(calcData).forEach(([key, value]) => {
    if (value.count) rate[key] = value.total / value.count
  })

  return { rates, rate, userVoted, users: judges.length }
}

export const getProposalAllRateForSpecialists = (
  users,
  proposalId,
  ratingCriteria
) => {
  const specialists = users.filter(value => value.role === 'specialist')
  const usersHaveRate = specialists.filter(value =>
    _.get(value, `proposals.${proposalId}.rate`, false)
  )

  if (usersHaveRate.length === 0) {
    return { rates: [], rate: {}, userVoted: 0, users: specialists.length }
  }

  const rates = generateRatesData(ratingCriteria, usersHaveRate, proposalId)
  const userVoted = usersHaveRate.length
  const calcData = generateCalcData(ratingCriteria)
  const rate = {}

  usersHaveRate.forEach(user => {
    const userRate = user.proposals[proposalId].rate
    Object.entries(userRate).forEach(([key, value]) => {
      if (value && calcData[key]) {
        calcData[key].count++
        calcData[key].total += value
      }
    })
  })

  Object.entries(calcData).forEach(([key, value]) => {
    if (value.count) rate[key] = value.total / value.count
  })

  return { rates, rate, userVoted, users: specialists.length }
}

const useProposalAllRateForSpecialists = (users, proposalId) => {
  const [proposalRateData, setProposalRateData] = useState({
    rates: [],
    rate: {},
    userVoted: 0,
    users: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { ratingCriteria } = useRatingCriteria('specialists-rating-criteria')

  const memoizedUsers = useMemo(() => users, [users])
  const memoizedProposalId = useMemo(() => proposalId, [proposalId])
  const memoizedRatingCriteria = useMemo(() => ratingCriteria, [ratingCriteria])

  useEffect(() => {
    let isMounted = true

    const fetchProposalRateData = async () => {
      try {
        const data = getProposalAllRateForSpecialists(
          memoizedUsers || [],
          memoizedProposalId,
          memoizedRatingCriteria
        )
        if (isMounted) {
          setProposalRateData(data)
        }
      } catch (err) {
        if (isMounted) {
          setError(err)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchProposalRateData()

    return () => {
      isMounted = false
    }
  }, [memoizedUsers, memoizedProposalId, memoizedRatingCriteria])

  return { proposalRateData, loading, error }
}

export { useProposalAllRateForSpecialists }

const useProposalAllRate = (users, proposalId) => {
  const [proposalRateData, setProposalRateData] = useState({
    rates: [],
    rate: {},
    userVoted: 0,
    users: 0
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { ratingCriteria } = useRatingCriteria()

  const memoizedUsers = useMemo(() => users, [users])
  const memoizedProposalId = useMemo(() => proposalId, [proposalId])
  const memoizedRatingCriteria = useMemo(() => ratingCriteria, [ratingCriteria])

  useEffect(() => {
    let isMounted = true

    const fetchProposalRateData = async () => {
      try {
        const data = await getProposalAllRate(
          memoizedUsers,
          memoizedProposalId,
          memoizedRatingCriteria
        )
        if (isMounted) {
          setProposalRateData(data)
        }
      } catch (err) {
        if (isMounted) {
          setError(err)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchProposalRateData()

    return () => {
      isMounted = false
    }
  }, [memoizedUsers, memoizedProposalId, memoizedRatingCriteria])

  return { proposalRateData, loading, error }
}

export default useProposalAllRate
